// Reusable JSON-LD builders for local SEO.

export const SITE_URL = "https://e-bikeandco.com";
export const AVG_RATING = "4.9";

// Catalogue produits standard (location vélos) avec prix indicatifs jour.
export const BIKE_PRODUCTS: { name: string; description: string; price: string }[] = [
  { name: "VTT électrique", description: "VTT à assistance électrique adapté aux chemins forestiers et sentiers landais.", price: "35" },
  { name: "VTC électrique", description: "Vélo tout-chemin électrique, idéal pour les pistes cyclables et la Vélodyssée.", price: "30" },
  { name: "Vélo enfant", description: "Vélos enfants de toutes tailles (12 à 24 pouces).", price: "10" },
  { name: "Fatbike", description: "Vélo à pneus larges adapté à la plage et au sable.", price: "40" },
  { name: "Remorque enfant", description: "Remorque biplace pour transporter les jeunes enfants.", price: "12" },
  { name: "Siège bébé", description: "Siège bébé sécurisé compatible avec nos vélos adultes.", price: "5" },
];

// NOTE: le balisage `Product` a été retiré des pages ville : Google exigeait
// `review`/`aggregateRating` par produit, alors que la note 4,9/5 (300+ avis)
// est une note d'entreprise. Les catalogues utilisent `Service` sous
// `LocalBusiness`/`Service`, qui portent déjà la note globale.


// JSON-LD ImageObject pour une hero image géolocalisée.
// `imagePath` doit commencer par "/" (chemin asset après build) ou être déjà absolu.
export function heroImageJsonLd(opts: {
  imagePath: string;
  caption: string;
  name?: string;
  lat: number;
  lng: number;
  locationName: string;
  pageUrl: string;
  width?: number;
  height?: number;
}) {
  const isAbsolute = /^https?:\/\//.test(opts.imagePath);
  const contentUrl = isAbsolute ? opts.imagePath : `${SITE_URL}${opts.imagePath}`;
  return {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    contentUrl,
    url: contentUrl,
    name: opts.name ?? opts.caption,
    caption: opts.caption,
    description: opts.caption,
    width: opts.width ?? 1920,
    height: opts.height ?? 1080,
    representativeOfPage: true,
    contentLocation: {
      "@type": "Place",
      name: opts.locationName,
      geo: { "@type": "GeoCoordinates", latitude: opts.lat, longitude: opts.lng },
    },
    creator: { "@type": "Organization", name: "eBike & Co", url: SITE_URL },
    copyrightHolder: { "@type": "Organization", name: "eBike & Co" },
    copyrightNotice: `© ${new Date().getFullYear()} eBike & Co`,
    creditText: "eBike & Co",
    license: `${SITE_URL}/mentions-legales`,
    acquireLicensePage: `${SITE_URL}/contact`,

    isPartOf: { "@type": "WebPage", url: `${SITE_URL}${opts.pageUrl}` },
  };
}

export interface BreadcrumbCrumb {
  label: string;
  href?: string;
}

export function breadcrumbJsonLd(items: BreadcrumbCrumb[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.label,
      ...(it.href ? { item: `${SITE_URL}${it.href}` } : {}),
    })),
  };
}

export interface FaqItem {
  question: string;
  answer: string;
}

export function faqJsonLd(items: FaqItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((q) => ({
      "@type": "Question",
      name: q.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: q.answer,
      },
    })),
  };
}

export interface ServiceParams {
  name: string;
  description: string;
  url: string;
  areaServed: string[]; // city names
  providerName?: string;
  providerTel?: string;
  providerAddress?: {
    streetAddress: string;
    addressLocality: string;
    postalCode: string;
  };
  priceRange?: string;
}

export function bikeRentalServiceJsonLd(p: ServiceParams) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Location de vélos électriques et classiques",
    name: p.name,
    description: p.description,
    url: `${SITE_URL}${p.url}`,
    areaServed: p.areaServed.map((city) => ({
      "@type": "City",
      name: city,
      containedInPlace: { "@type": "AdministrativeArea", name: "Landes, France" },
    })),
    provider: {
      "@type": "LocalBusiness",
      name: p.providerName ?? "eBike & Co",
      ...(p.providerTel ? { telephone: p.providerTel } : {}),
      ...(p.providerAddress
        ? {
            address: {
              "@type": "PostalAddress",
              streetAddress: p.providerAddress.streetAddress,
              addressLocality: p.providerAddress.addressLocality,
              postalCode: p.providerAddress.postalCode,
              addressCountry: "FR",
            },
          }
        : {}),
    },
    ...(p.priceRange ? { priceRange: p.priceRange } : {}),
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Catalogue de location",
      itemListElement: BIKE_PRODUCTS.map((prod) => ({
        "@type": "Offer",
        priceCurrency: "EUR",
        price: prod.price,
        availability: "https://schema.org/InStock",
        url: `${SITE_URL}${p.url}`,
        itemOffered: {
          "@type": "Service",
          name: prod.name,
          description: prod.description,
          provider: { "@type": "Organization", name: "eBike & Co" },
        },
      })),
    },
  };
}

// Shared FAQ used on the homepage (also fed to FAQPage JSON-LD).
export const homepageFaq: FaqItem[] = [
  {
    question: "Peut-on se faire livrer au camping ?",
    answer:
      "Oui ! Nous livrons dans la plupart des campings, gîtes et hôtels de la côte landaise. Indiquez simplement votre lieu d'hébergement lors de la réservation.",
  },
  {
    question: "Que se passe-t-il en cas de panne ?",
    answer:
      "Notre équipe locale intervient rapidement. Appelez-nous et nous venons vous dépanner ou remplacer le vélo dans les meilleurs délais.",
  },
  {
    question: "Faut-il réserver à l'avance ?",
    answer:
      "Fortement recommandé, surtout en juillet-août. Les stocks partent vite ! Réserver à l'avance vous garantit la disponibilité et le choix du matériel.",
  },
  {
    question: "Proposez-vous des vélos enfants ?",
    answer:
      "Absolument ! Nous avons des vélos adaptés à tous les âges : sièges bébé, remorques, vélos enfants de toutes tailles, et même des tandems.",
  },
];
